import React, { useEffect, useState } from "react";
import { Loader2, MapPin, RefreshCw } from "lucide-react";
import api from "@/lib/api";

/**
 * JubelioRegionPicker — cascading dropdown Province → City → District → Area
 *
 * Props:
 *   value: { province_id, city_id, district_id, area_id, province, city, district, area, postal_code }
 *   onChange: (next) => void
 *   adminMode: bool — pakai endpoint /admin/jubelio/regions/* (default: /jubelio/regions/*)
 *   compact: bool — layout 1 kolom (mobile / sidebar)
 */
export default function JubelioRegionPicker({ value = {}, onChange, adminMode = false, compact = false }) {
  const base = adminMode ? "/admin/jubelio/regions" : "/jubelio/regions";

  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState({ p: false, c: false, d: false, a: false });
  const [error, setError] = useState(null);

  const fetchList = async (key, url, setter) => {
    setLoading((l) => ({ ...l, [key]: true }));
    setError(null);
    try {
      const { data } = await api.get(url);
      setter(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Gagal memuat daftar wilayah";
      setError(msg);
      setter([]);
    } finally {
      setLoading((l) => ({ ...l, [key]: false }));
    }
  };

  useEffect(() => { fetchList("p", `${base}/provinces`, setProvinces); }, []);  // eslint-disable-line
  useEffect(() => {
    if (value.province_id) fetchList("c", `${base}/cities?province_id=${value.province_id}`, setCities);
    else setCities([]);
  }, [value.province_id]);  // eslint-disable-line
  useEffect(() => {
    if (value.city_id) fetchList("d", `${base}/districts?city_id=${value.city_id}`, setDistricts);
    else setDistricts([]);
  }, [value.city_id]);  // eslint-disable-line
  useEffect(() => {
    if (value.district_id) fetchList("a", `${base}/areas?district_id=${value.district_id}`, setAreas);
    else setAreas([]);
  }, [value.district_id]);  // eslint-disable-line

  const setField = (patch) => onChange?.({ ...value, ...patch });

  const onProvince = (pid) => {
    const p = provinces.find((x) => String(x.province_id) === String(pid));
    setField({
      province_id: pid || "",
      province: p?.name || "",
      city_id: "", city: "",
      district_id: "", district: "",
      area_id: "", area: "",
    });
  };
  const onCity = (cid) => {
    const c = cities.find((x) => String(x.city_id) === String(cid));
    setField({
      city_id: cid || "", city: c?.name || "",
      district_id: "", district: "",
      area_id: "", area: "",
    });
  };
  const onDistrict = (did) => {
    const d = districts.find((x) => String(x.district_id) === String(did));
    setField({
      district_id: did || "", district: d?.name || "",
      area_id: "", area: "",
    });
  };
  const onArea = (aid) => {
    const a = areas.find((x) => String(x.area_id) === String(aid));
    setField({
      area_id: aid || "",
      area: a?.name || "",
      subdistrict: a?.name || value.subdistrict || "",
      postal_code: a?.zipcode || value.postal_code || "",
    });
  };

  const Select = ({ label, value: v, options, onChange: oc, getId, getLabel, disabled, busy, testid }) => (
    <div>
      <label className="text-xs font-bold text-[#5C5C5C] uppercase tracking-wider flex items-center gap-1">
        {label}
        {busy && <Loader2 size={11} className="animate-spin" />}
      </label>
      <select
        value={v || ""}
        onChange={(e) => oc(e.target.value)}
        disabled={disabled || busy}
        className="w-full mt-1 border border-[#E5E5E5] rounded-lg p-2 text-sm bg-white disabled:bg-[#FAF9F6] disabled:cursor-not-allowed"
        data-testid={testid}
      >
        <option value="">— Pilih {label} —</option>
        {options.map((o) => (
          <option key={getId(o)} value={getId(o)}>{getLabel(o)}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className={compact ? "space-y-3" : "grid grid-cols-1 md:grid-cols-2 gap-3"} data-testid="jubelio-region-picker">
      <Select
        label="Provinsi"
        value={value.province_id}
        options={provinces}
        onChange={onProvince}
        getId={(o) => o.province_id}
        getLabel={(o) => o.name}
        busy={loading.p}
        testid="region-province-select"
      />
      <Select
        label="Kota / Kabupaten"
        value={value.city_id}
        options={cities}
        onChange={onCity}
        getId={(o) => o.city_id}
        getLabel={(o) => o.name}
        disabled={!value.province_id}
        busy={loading.c}
        testid="region-city-select"
      />
      <Select
        label="Kecamatan"
        value={value.district_id}
        options={districts}
        onChange={onDistrict}
        getId={(o) => o.district_id}
        getLabel={(o) => o.name}
        disabled={!value.city_id}
        busy={loading.d}
        testid="region-district-select"
      />
      <Select
        label="Kelurahan / Area"
        value={value.area_id}
        options={areas}
        onChange={onArea}
        getId={(o) => o.area_id}
        getLabel={(o) => `${o.name}${o.zipcode ? ` (${o.zipcode})` : ""}`}
        disabled={!value.district_id}
        busy={loading.a}
        testid="region-area-select"
      />
      {value.area_id && (
        <div className="md:col-span-2 text-xs text-[#2D6A4F] bg-[#2D6A4F]/5 border border-[#2D6A4F]/20 rounded-lg px-2 py-1.5 flex items-center gap-1.5" data-testid="region-summary">
          <MapPin size={12} />
          {adminMode ? (
            <span><b>area_id={value.area_id}</b> · {value.area || ""}{value.postal_code ? ` · ${value.postal_code}` : ""}</span>
          ) : (
            <span>Wilayah terpilih: <b>{value.area || ""}</b>{value.postal_code ? ` · ${value.postal_code}` : ""}</span>
          )}
        </div>
      )}
      {error && (
        <div className="md:col-span-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
          <RefreshCw size={12} /> {error}
        </div>
      )}
    </div>
  );
}
